import React, { useEffect, useMemo, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import formate from 'date-format'
import Button from '../../components/shared/Button'
import Loader from '../../components/shared/Loader'
import Record from '../../components/shared/Record'
import { EditIcon } from '../../components/shared/Icons'
import useFetch from '../../hooks/useFetch'
import ViewLayout from '../../layouts/ViewLayout'
import {
  downloadQuoteAttachment,
  getAdminQuoteById,
  updateAdminQuotePrice,
  updateAdminQuoteStatus,
} from '../../utils/Apis/quotes'
import { withErrorHandling } from '../../utils/withErrorHandling'
import { formatQuoteType, QuoteStatusBadge } from './COLUMNS'

const editablePriceFields = [
  { key: 'pcbCost', label: 'PCB Cost / Service Cost' },
  { key: 'basePcbCost', label: 'Base PCB Cost' },
  { key: 'advancedOptionsCost', label: 'Advanced Options Cost' },
  { key: 'engineeringFee', label: 'Engineering Fee' },
  { key: 'shippingCost', label: 'Shipping Cost' },
  { key: 'discountAmount', label: 'Discount Amount' },
  { key: 'totalAmount', label: 'Total Amount' },
  { key: 'leadTimeDays', label: 'Lead Time Days' },
]

const statusOptions = ['draft', 'calculated', 'submitted', 'approved', 'rejected', 'ordered']

/**
 * 判断是否为简单可展示值。
 * @param {unknown} value 原始值
 * @returns {boolean} 是否简单值
 */
const isSimpleValue = (value) => {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}

/**
 * 将对象转换为扁平参数行。
 * @param {Object} source 源对象
 * @param {string} prefix 字段前缀
 * @returns {Array<{key: string, value: unknown}>} 参数行
 */
const flattenObject = (source = {}, prefix = '') => {
  return Object.entries(source || {}).flatMap(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key
    if (isSimpleValue(value)) {
      return [{ key: nextKey, value }]
    }
    if (Array.isArray(value)) {
      return [{ key: nextKey, value: value.join(', ') }]
    }
    return flattenObject(value, nextKey)
  })
}

/**
 * 格式化简单值用于展示。
 * @param {unknown} value 原始值
 * @returns {string} 展示文本
 */
const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

/**
 * 后台报价单详情页。
 */
const ViewQuote = () => {
  const { id } = useParams()
  const [status, setStatus] = useState('')
  const [adminRemark, setAdminRemark] = useState('')
  const [pricingForm, setPricingForm] = useState({})

  const { data, error, loading, refetch } = useFetch({
    fn: () => getAdminQuoteById(id),
  })

  const specRows = useMemo(
    () => flattenObject(Object.keys(data?.spec || {}).length ? data?.spec : data?.pcbSpec || {}),
    [data],
  )

  /**
   * 更新状态表单字段。
   * @param {React.ChangeEvent<HTMLSelectElement|HTMLTextAreaElement>} e 输入事件
   */
  const handleStatusChange = (e) => {
    const { name, value } = e.target
    if (name === 'status') setStatus(value)
    if (name === 'adminRemark') setAdminRemark(value)
  }

  /**
   * 更新价格表单字段。
   * @param {React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>} e 输入事件
   */
  const handlePricingChange = (e) => {
    const { name, value } = e.target
    setPricingForm((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleUpdateStatus = withErrorHandling(async () => {
    const response = await updateAdminQuoteStatus({
      id,
      payload: {
        status,
        adminRemark,
      },
    })
    if (response?.success) {
      toast.success('Quote status updated')
      refetch()
    }
  })

  const handleUpdatePrice = withErrorHandling(async () => {
    const response = await updateAdminQuotePrice({
      id,
      payload: {
        pricing: pricingForm,
        adminRemark,
      },
    })
    if (response?.success) {
      toast.success('Quote price updated')
      refetch()
    }
  })

  /**
   * 下载报价附件。
   * @param {number} attachmentIndex 附件索引
   * @param {string} fileName 文件名
   */
  const handleDownloadAttachment = withErrorHandling(async (attachmentIndex, fileName) => {
    await downloadQuoteAttachment({
      quoteId: id,
      attachmentIndex,
      fileName,
    })
  })

  useEffect(() => {
    document.title = 'Instant Quote Details - Admin'
  }, [])

  useEffect(() => {
    if (!data?._id) return
    setStatus(data.status || '')
    setAdminRemark(data.adminRemark || '')
    setPricingForm({
      pcbCost: data.pricing?.pcbCost ?? '',
      basePcbCost: data.pricing?.basePcbCost ?? '',
      advancedOptionsCost: data.pricing?.advancedOptionsCost ?? '',
      engineeringFee: data.pricing?.engineeringFee ?? '',
      shippingCost: data.pricing?.shippingCost ?? '',
      discountAmount: data.pricing?.discountAmount ?? '',
      totalAmount: data.pricing?.totalAmount ?? '',
      leadTimeDays: data.pricing?.leadTimeDays ?? '',
      shippingMethod: data.pricing?.shippingMethod ?? '',
    })
  }, [data])

  if (loading) return <Loader />
  if (error) return <div>Error: {error.message}</div>

  return (
    <ViewLayout
      sectionTitle={'Instant Quote Details'}
      allButtonPath={'/quotes'}
      allButtonTitle={'All Quotes'}
      className={'gap-8 md:flex md:flex-wrap lg:px-10'}
    >
      <Column title={'Basic Information'}>
        <Record label={'Quote No'} value={data?.quoteNo} />
        <Record label={'Quote Type'} value={formatQuoteType(data?.quoteType)} />
        <Record label={'Status'} value={<QuoteStatusBadge status={data?.status} />} />
        <Record
          label={'Created At'}
          value={data?.createdAt ? formate.asString('dd/MM/yyyy HH:mm', new Date(data.createdAt)) : '-'}
        />
        <Record
          label={'Submitted At'}
          value={data?.submittedAt ? formate.asString('dd/MM/yyyy HH:mm', new Date(data.submittedAt)) : '-'}
        />
        {data?.orderId?._id ? (
          <Record
            label={'Linked Order'}
            value={
              <NavLink className="text-blue-600 hover:underline" to={`/orders/view/${data.orderId._id}`}>
                {data.orderId._id}
              </NavLink>
            }
          />
        ) : (
          <Record label={'Linked Order'} value={'-'} />
        )}
      </Column>

      <Column title={'Customer'}>
        <Record label={'Name'} value={data?.customerInfo?.name || data?.user?.name} />
        <Record label={'Email'} value={data?.customerInfo?.email || data?.user?.email} />
        <Record label={'Phone'} value={data?.customerInfo?.phone} />
        <Record label={'Company'} value={data?.customerInfo?.company} />
        <Record label={'Country Code'} value={data?.customerInfo?.countryCode} />
      </Column>

      <Column title={'Pricing'}>
        <Record label={'Currency'} value={data?.pricing?.currency || 'USD'} />
        <Record label={'PCB Cost'} value={`$${Number(data?.pricing?.pcbCost || 0).toFixed(2)}`} />
        <Record label={'Engineering Fee'} value={`$${Number(data?.pricing?.engineeringFee || 0).toFixed(2)}`} />
        <Record label={'Shipping Cost'} value={`$${Number(data?.pricing?.shippingCost || 0).toFixed(2)}`} />
        <Record label={'Discount'} value={`$${Number(data?.pricing?.discountAmount || 0).toFixed(2)}`} />
        <Record label={'Total'} value={`$${Number(data?.pricing?.totalAmount || 0).toFixed(2)}`} />
        <Record label={'Lead Time'} value={`${data?.pricing?.leadTimeDays || 0} days`} />
        <Record label={'Shipping Method'} value={data?.pricing?.shippingMethod} />
      </Column>

      <div className="w-full">
        <h2 className="mb-3 text-2xl font-semibold">Quote Parameters</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {specRows.map((row) => (
            <div key={row.key} className="rounded border border-primary/10 p-3">
              <p className="text-xs text-muted-foreground">{row.key}</p>
              <p className="break-words text-sm">{formatValue(row.value)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2">
        <Panel title={'Review Flags'}>
          {(data?.reviewFlags || []).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.reviewFlags.map((flag) => (
                <span key={flag} className="rounded bg-orange-100 px-2 py-1 text-xs text-orange-700">
                  {flag}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No review flags</p>
          )}
        </Panel>

        <Panel title={'Warnings'}>
          {(data?.warnings || []).length > 0 ? (
            <ul className="list-disc pl-5 text-sm">
              {data.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No warnings</p>
          )}
        </Panel>
      </div>

      <div className="w-full">
        <h2 className="mb-3 text-2xl font-semibold">Gerber / File Summary</h2>
        {data?.gerberSummary && Object.keys(data.gerberSummary).length > 0 ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {flattenObject(data.gerberSummary).map((row) => (
              <div key={row.key} className="rounded border border-primary/10 p-3">
                <p className="text-xs text-muted-foreground">{row.key}</p>
                <p className="break-words text-sm">{formatValue(row.value)}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No file summary</p>
        )}
      </div>

      <div className="w-full">
        <h2 className="mb-3 text-2xl font-semibold">Attachments</h2>
        {(data?.attachments || []).length > 0 ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.attachments.map((attachment, index) => (
              <div key={`${attachment.storedName}-${index}`} className="flex items-center justify-between rounded border border-primary/10 p-3">
                <div>
                  <p className="font-medium">{attachment.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {attachment.mimeType || '-'} / {Number(attachment.size || 0)} bytes
                  </p>
                </div>
                <Button onClick={() => handleDownloadAttachment(index, attachment.name)}>
                  Download
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No attachments</p>
        )}
      </div>

      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2">
        <Panel title={'Update Status'}>
          <div className="flex flex-col gap-3">
            <select
              name="status"
              value={status}
              onChange={handleStatusChange}
              className="rounded-md border border-primary/20 p-2"
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <textarea
              name="adminRemark"
              rows="4"
              value={adminRemark}
              onChange={handleStatusChange}
              className="rounded-md border border-primary/20 p-2"
              placeholder="Admin remark"
            />
            <Button onClick={handleUpdateStatus}>
              <EditIcon />
              Save Status
            </Button>
          </div>
        </Panel>

        <Panel title={'Manual Price Adjustment'}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {editablePriceFields.map((field) => (
              <label key={field.key} className="flex flex-col gap-1 text-sm">
                {field.label}
                <input
                  type="number"
                  step="0.01"
                  name={field.key}
                  value={pricingForm[field.key] ?? ''}
                  onChange={handlePricingChange}
                  className="rounded-md border border-primary/20 p-2"
                />
              </label>
            ))}
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              Shipping Method
              <input
                type="text"
                name="shippingMethod"
                value={pricingForm.shippingMethod ?? ''}
                onChange={handlePricingChange}
                className="rounded-md border border-primary/20 p-2"
              />
            </label>
          </div>
          <Button className="mt-3" onClick={handleUpdatePrice}>
            <EditIcon />
            Save Price
          </Button>
        </Panel>
      </div>
    </ViewLayout>
  )
}

const Column = ({ title, children }) => {
  return (
    <div className="flex min-w-[260px] flex-col gap-2">
      <h2 className="text-2xl font-semibold">{title}</h2>
      {children}
    </div>
  )
}

const Panel = ({ title, children }) => {
  return (
    <div className="rounded border border-primary/10 p-4">
      <h2 className="mb-3 text-xl font-semibold">{title}</h2>
      {children}
    </div>
  )
}

export default ViewQuote
